import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './WorkoutProgressionModule.css';

const WorkoutProgressionModule = () => {
    const navigate = useNavigate();
    const [progressionStats, setProgressionStats] = useState({
        attackBonus: 0,
        attackCostReduction: 0,
        defendBonus: 0,
        defendCostReduction: 0,
        totalReps: 0,
        hasWorkouts: false
    });
    const [bonusesApplied, setBonusesApplied] = useState(false);

    useEffect(() => {
        calculateProgressionStats();
    }, []);

    const calculateProgressionStats = () => {
        const workouts = JSON.parse(localStorage.getItem('workoutHistory') || '[]');

        if (workouts.length === 0) {
            setProgressionStats({
                attackBonus: 0,
                attackCostReduction: 0,
                defendBonus: 0,
                defendCostReduction: 0,
                totalReps: 0,
                hasWorkouts: false
            });
            return;
        }

        let attackReps = 0;
        let defendReps = 0;
        let totalReps = 0;

        workouts.forEach(workout => {
            if (workout.exercises && workout.exercises.length > 0) {
                workout.exercises.forEach(exercise => {
                    const muscleGroup = getMuscleGroupFromExercise(exercise);
                    const reps = workout.repCount || 10;
                    totalReps += reps;

                    // Muscle group mapping based on your specification
                    switch (muscleGroup.toLowerCase()) {
                        case 'chest':  // Push moves -> Attack
                        case 'legs':   // Legs -> Attack
                            attackReps += reps;
                            break;
                        case 'back':      // Pull moves -> Defend
                        case 'core':      // Core -> Defend
                        case 'shoulders': // Shoulders -> Defend
                        case 'arms':      // Arms -> Defend
                        case 'other':     // Other -> Defend
                        default:          // All other groups -> Defend
                            defendReps += reps;
                            break;
                    }
                });
            }
        });

        // Calculate bonuses - every 10 reps gives +1 points and -1 cost
        const attackBonusSets = Math.floor(attackReps / 10);
        const defendBonusSets = Math.floor(defendReps / 10);

        setProgressionStats({
            attackBonus: attackBonusSets * 1,
            attackCostReduction: attackBonusSets * 1,
            defendBonus: defendBonusSets * 1,
            defendCostReduction: defendBonusSets * 1,
            totalReps,
            hasWorkouts: true
        });
    };

    const getMuscleGroupFromExercise = (exercise) => {
        const exerciseName = typeof exercise === 'string' ? exercise : exercise.name;

        if (!exerciseName || typeof exerciseName !== 'string') {
            return 'Other';
        }

        const exerciseNameLowerCase = exerciseName.toLowerCase();

        // Chest (Push moves) -> Attack
        if (exerciseNameLowerCase.includes('push') || exerciseNameLowerCase.includes('chest') ||
            exerciseNameLowerCase.includes('bench') || exerciseNameLowerCase.includes('fly')) {
            return 'Chest';
        }

        // Back (Pull moves) -> Defend
        if (exerciseNameLowerCase.includes('pull') || exerciseNameLowerCase.includes('row') ||
            exerciseNameLowerCase.includes('chin') || exerciseNameLowerCase.includes('lat') ||
            exerciseNameLowerCase.includes('deadlift')) {
            return 'Back';
        }

        // Legs -> Attack
        if (exerciseNameLowerCase.includes('squat') || exerciseNameLowerCase.includes('lunge') ||
            exerciseNameLowerCase.includes('leg') || exerciseNameLowerCase.includes('calf')) {
            return 'Legs';
        }

        // Arms -> Defend (default for arms)
        if (exerciseNameLowerCase.includes('curl') || exerciseNameLowerCase.includes('tricep') ||
            exerciseNameLowerCase.includes('bicep') || exerciseNameLowerCase.includes('dip')) {
            return 'Arms';
        }

        // Shoulders -> Defend
        if (exerciseNameLowerCase.includes('shoulder') || exerciseNameLowerCase.includes('raise') ||
            exerciseNameLowerCase.includes('shrug')) {
            return 'Shoulders';
        }

        // Core -> Defend
        if (exerciseNameLowerCase.includes('crunch') || exerciseNameLowerCase.includes('plank') ||
            exerciseNameLowerCase.includes('twist') || exerciseNameLowerCase.includes('ab') ||
            exerciseNameLowerCase.includes('core')) {
            return 'Core';
        }

        return 'Other';
    };

    const handleNavigateToTrain = () => {
        navigate('/train-page');
    };

    const handleNavigateToBuild = () => {
        navigate('/build-page');
    };

    const handleApplyBonuses = () => {
        setBonusesApplied(true);
    };

    const handleResetView = () => {
        setBonusesApplied(false);
    };

    if (!progressionStats.hasWorkouts) {
        return (
            <div className="workout-progression-container">
                <div className="progression-header">
                    <h3 className="progression-title">Card Power Bonus</h3>
                    <p className="progression-subtitle">Complete workouts to unlock card bonuses</p>
                </div>

                <div className="progression-grid">
                    <div className="progression-quadrant defend-quadrant">
                        <div className="quadrant-label">DEFEND</div>
                        <div className="quadrant-value">+0</div>
                    </div>
                    <div className="progression-quadrant cost-quadrant">
                        <div className="quadrant-label">COST</div>
                        <div className="quadrant-value">-0</div>
                    </div>
                    <div className="progression-quadrant attack-quadrant">
                        <div className="quadrant-label">ATTACK</div>
                        <div className="quadrant-value">+0</div>
                    </div>
                    <div className="progression-quadrant cost-quadrant">
                        <div className="quadrant-label">COST</div>
                        <div className="quadrant-value">-0</div>
                    </div>
                </div>

                <div className="call-to-action">
                    <p className="cta-text">Want more power on this card?</p>
                    <div className="button-container">
                        <button
                            className="cta-button train-button"
                            onClick={handleNavigateToTrain}
                        >
                            TRAIN
                        </button>
                        <button
                            className="cta-button build-button"
                            onClick={handleNavigateToBuild}
                        >
                            BUILD
                        </button>
                    </div>
                    <p className="small-text">Save workouts to unlock bonuses</p>
                </div>
            </div>
        );
    }

    if (bonusesApplied) {
        return (
            <div className="workout-progression-container">
                <div className="progression-header">
                    <h3 className="progression-title">Bonuses Applied!</h3>
                    <p className="progression-subtitle">Use these bonuses with your paper scorecard</p>
                </div>

                <div className="bonuses-applied-message">
                    <div className="applied-icon">✅</div>
                    <p className="applied-text">
                        Your bonuses have been applied to the current game.
                        Write these values on your paper scorecard and use them during battle!
                    </p>
                    <p className="instruction-text">
                        Complete more workouts to earn bonuses for your next game.
                    </p>

                    <div className="button-container">
                        <button
                            className="reset-button"
                            onClick={handleResetView}
                        >
                            View Available Bonuses
                        </button>
                        <button
                            className="progress-button train-button"
                            onClick={handleNavigateToTrain}
                        >
                            TRAIN MORE
                        </button>
                        <button
                            className="progress-button build-button"
                            onClick={handleNavigateToBuild}
                        >
                            BUILD MORE
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="workout-progression-container">
            <div className="progression-header">
                <h3 className="progression-title">Card Power Bonus</h3>
                <p className="progression-subtitle">From {progressionStats.totalReps} total reps</p>
            </div>

            <div className="progression-grid">
                <div className="progression-quadrant defend-quadrant">
                    <div className="quadrant-label">DEFEND</div>
                    <div className="quadrant-value">+{progressionStats.defendBonus}</div>
                </div>
                <div className="progression-quadrant cost-quadrant">
                    <div className="quadrant-label">COST</div>
                    <div className="quadrant-value">-{progressionStats.defendCostReduction}</div>
                </div>
                <div className="progression-quadrant attack-quadrant">
                    <div className="quadrant-label">ATTACK</div>
                    <div className="quadrant-value">+{progressionStats.attackBonus}</div>
                </div>
                <div className="progression-quadrant cost-quadrant">
                    <div className="quadrant-label">COST</div>
                    <div className="quadrant-value">-{progressionStats.attackCostReduction}</div>
                </div>
            </div>

            <div className="apply-bonuses-section">
                <button
                    className="apply-bonuses-button"
                    onClick={handleApplyBonuses}
                >
                    Apply Bonuses to Current Game
                </button>
                <p className="apply-instruction">
                    Click to use these bonuses with your paper scorecard
                </p>
            </div>

            <div className="progress-info">
                <p className="progress-text">
                    Complete more workouts to increase your card bonuses!
                </p>
                <div className="button-container">
                    <button
                        className="progress-button train-button"
                        onClick={handleNavigateToTrain}
                    >
                        TRAIN MORE
                    </button>
                    <button
                        className="progress-button build-button"
                        onClick={handleNavigateToBuild}
                    >
                        BUILD MORE
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WorkoutProgressionModule;